import { type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { Sinnbild } from './Sinnbild';

/**
 * Die Schwellen der Ladeanzeige (NFA-01, Auftrag CRM-165) — an EINER Stelle.
 *
 * Bis `langsam` steht nur das Skelett; danach sagt ein kleines Zeichen, dass
 * es länger dauert; ab `aufgeben` steht „Konnte nicht geladen werden" mit
 * „Erneut versuchen". Aufgegeben wird nur in der Anzeige: Die Anfrage läuft
 * weiter, und kommt die Antwort doch noch, gilt sie.
 */
export const LADESCHWELLEN = {
  langsamMs: 3_000,
  aufgebenMs: 15_000,
} as const;

/** `skelett` → `langsam` → `gescheitert`, oder `fertig`, sobald die Antwort da ist. */
export type Ladestufe = 'skelett' | 'langsam' | 'gescheitert' | 'fertig';

export type Ladezustand<T> = {
  readonly stufe: Ladestufe;
  /** Die Antwort, sobald sie da ist — auch eine späte nach `gescheitert`. */
  readonly daten: T | undefined;
  /** Der Fehler der letzten Anfrage, falls sie gescheitert ist (nicht bei Zeitablauf). */
  readonly fehler: unknown;
  /** Bricht die laufende Anfrage ab und stellt sie neu — nie zwei zugleich. */
  readonly erneut: () => void;
};

/**
 * Lädt mit drei Stufen (Auftrag CRM-165).
 *
 * `laden` bekommt ein AbortSignal und gibt es an `rufe` weiter. Ändert sich
 * `schluessel`, wird neu geladen; die alte Anfrage wird abgebrochen, ihre
 * Antwort zählt nicht mehr. Beim Verlassen der Maske ebenso.
 *
 * `laden` darf bei jedem Zeichnen neu entstehen: Der Hook liest es über
 * einen Verweis und lädt nur bei neuem `schluessel` oder „Erneut versuchen".
 */
export function useLadestufe<T>(
  laden: (signal: AbortSignal) => Promise<T>,
  schluessel: string = '',
): Ladezustand<T> {
  const [stufe, setzeStufe] = useState<Ladestufe>('skelett');
  const [daten, setzeDaten] = useState<T | undefined>(undefined);
  const [fehler, setzeFehler] = useState<unknown>(undefined);
  const [runde, setzeRunde] = useState(0);
  const ladenRef = useRef(laden);
  ladenRef.current = laden;

  useEffect(() => {
    const abbruch = new AbortController();
    setzeStufe('skelett');
    setzeDaten(undefined);
    setzeFehler(undefined);
    const langsam = setTimeout(() => setzeStufe('langsam'), LADESCHWELLEN.langsamMs);
    const aufgeben = setTimeout(() => setzeStufe('gescheitert'), LADESCHWELLEN.aufgebenMs);
    const uhrenAus = (): void => {
      clearTimeout(langsam);
      clearTimeout(aufgeben);
    };

    ladenRef.current(abbruch.signal).then(
      (antwort) => {
        if (abbruch.signal.aborted) return;
        uhrenAus();
        setzeDaten(antwort);
        setzeStufe('fertig');
      },
      (ursache: unknown) => {
        if (abbruch.signal.aborted) return;
        uhrenAus();
        setzeFehler(ursache);
        setzeStufe('gescheitert');
      },
    );

    return () => {
      uhrenAus();
      abbruch.abort();
    };
  }, [schluessel, runde]);

  const erneut = useCallback(() => setzeRunde((r) => r + 1), []);
  return { stufe, daten, fehler, erneut };
}

/**
 * Die Anzeige zu `useLadestufe`: Skelett, dann der Hinweis, dann die Absage
 * mit „Erneut versuchen". Bei `fertig` steht `children`.
 *
 * Das Zeichen dreht sich nur, wo Bewegung erwünscht ist
 * (prefers-reduced-motion, in stil.css).
 */
export function Ladeanzeige({
  zustand,
  skelett,
  was,
  klasse,
  children,
}: {
  readonly zustand: Pick<Ladezustand<unknown>, 'stufe' | 'erneut'>;
  /** Der Platzhalter in der Form dessen, was kommt — ohne Drehzeichen. */
  readonly skelett: ReactNode;
  /** Für Vorleser: was geladen wird, etwa „Die Kachel Offene Posten". */
  readonly was: string;
  /** Zusatzklasse der Absage, etwa `karte` im Raster des Dashboards. */
  readonly klasse?: string;
  readonly children?: ReactNode;
}): JSX.Element {
  if (zustand.stufe === 'fertig') return <>{children}</>;
  if (zustand.stufe === 'gescheitert') {
    return (
      <div className={`ladeanzeige ladeanzeige--gescheitert${klasse === undefined ? '' : ` ${klasse}`}`} role="alert">
        <p>Konnte nicht geladen werden.</p>
        <button className="knopf knopf--klein" type="button" onClick={zustand.erneut}>
          Erneut versuchen
        </button>
      </div>
    );
  }
  return (
    <div className="ladeanzeige" aria-busy="true">
      {skelett}
      {zustand.stufe === 'langsam' ? (
        <p className="ladeanzeige__langsam" role="status">
          <Sinnbild gewicht="light" name="circle-notch" klasse="ladeanzeige__zeichen" />
          Dauert länger als üblich …
        </p>
      ) : (
        <p className="nur-vorlesen" role="status">
          {was} wird geladen.
        </p>
      )}
    </div>
  );
}
